import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore, type Notification } from '@/stores/notificationStore';
import { clsx } from 'clsx';
import {
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  XMarkIcon,
  BellIcon,
} from '@heroicons/react/24/outline';

const typeStyles: Record<Notification['type'], string> = {
  info: 'bg-blue-50 border-blue-200 text-blue-800',
  success: 'bg-green-50 border-green-200 text-green-800',
  warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  error: 'bg-red-50 border-red-200 text-red-800',
};

const typeIcons: Record<Notification['type'], typeof InformationCircleIcon> = {
  info: InformationCircleIcon,
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  error: XCircleIcon,
};

export function NotificationCenter() {
  const navigate = useNavigate();
  const { notifications, isOpen, setIsOpen, markAsRead, markAllAsRead, removeNotification, unreadCount } =
    useNotificationStore();

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, setIsOpen]);

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      setIsOpen(false);
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (minutes < 1) return 'עכשיו';
    if (minutes < 60) return `לפני ${minutes} דקות`;
    if (hours < 24) return `לפני ${hours} שעות`;
    return date.toLocaleDateString('he-IL');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" onClick={() => setIsOpen(false)}>
      <div
        className="absolute left-4 top-16 w-96 max-h-[80vh] bg-white rounded-lg shadow-xl border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
        role="dialog"
        aria-live="polite"
        aria-label="מרכז התראות"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-900">התראות</h3>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              סמן הכל כנקרא
            </button>
          )}
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto max-h-[60vh]">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <BellIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>אין התראות חדשות</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={clsx(
                  'p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors',
                  !notification.read && 'bg-blue-50/30'
                )}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={clsx(
                      'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                      typeStyles[notification.type]
                    )}
                  >
                    {(() => {
                      const Icon = typeIcons[notification.type];
                      return <Icon className="w-4 h-4" />;
                    })()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="font-medium text-gray-900 truncate">
                        {notification.title}
                      </h4>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNotification(notification.id);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                        aria-label="הסר התראה"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatTime(notification.createdAt)}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t bg-gray-50">
            <button
              onClick={() => {
                useNotificationStore.getState().clearAll();
                setIsOpen(false);
              }}
              className="w-full text-center text-sm text-gray-600 hover:text-gray-800"
            >
              נקה הכל
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Notification bell button for the header
 */
export function NotificationBell() {
  const { unreadCount, isOpen, setIsOpen } = useNotificationStore();

  return (
    <button
      onClick={() => setIsOpen(!isOpen)}
      className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
      aria-label={`התראות${unreadCount > 0 ? ` (${unreadCount} חדשות)` : ''}`}
    >
      <svg
        className="w-6 h-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      </svg>
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
}
